/**
 * Stripe.js client utilities.
 *
 * Environment is derived from the `VITE_PAYMENTS_CLIENT_TOKEN` prefix
 * (`pk_test_` → sandbox, `pk_live_` → live). A missing/unknown token
 * throws — never silently defaults to live.
 */

import { loadStripe, type Stripe } from "@stripe/stripe-js";

export type StripeEnv = "sandbox" | "live";

const clientToken = import.meta.env.VITE_PAYMENTS_CLIENT_TOKEN as string | undefined;

export function getStripeEnvironment(): StripeEnv {
  if (clientToken?.startsWith("pk_test_")) return "sandbox";
  if (clientToken?.startsWith("pk_live_")) return "live";
  throw new Error(
    "Stripe payments are not configured for this build. " +
      "Complete Stripe go-live to enable production checkout.",
  );
}

export function isStripeConfigured(): boolean {
  return !!clientToken && (clientToken.startsWith("pk_test_") || clientToken.startsWith("pk_live_"));
}

let stripePromise: Promise<Stripe | null> | null = null;

export function getStripe(): Promise<Stripe | null> {
  if (!stripePromise) {
    getStripeEnvironment(); // throws if not configured
    stripePromise = loadStripe(clientToken as string);
  }
  return stripePromise;
}
